import { useMemo, useState } from "react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import classes from "./TimePickerPacket.module.css";
import { useBooking } from "../../../store/bookingStore";
import { parseLocalDateString } from "../../../util/parseLocalDate";
import {
  formatLocalDate,
  getAvailabilityRangesForDate,
  getBookingDurationHours,
  isBookingRangeAvailable,
} from "../../../util/studioAvailability";
import { calculatePreExtrasTotal } from "../../../util/calculatePreExtrasTotal";

const generateBlocksForDate = (dateString, durationHours, bookingStudioData) => {
  if (!dateString || !durationHours) return [];

  const ranges = getAvailabilityRangesForDate(dateString, bookingStudioData);
  const blocks = [];

  ranges.forEach((range) => {
    const { start, end } = range;
    const startDate = new Date(`2000-01-01T${start}`);
    const endDate = new Date(
      `2000-01-01T${end === "00:00" || end === "24:00" ? "24:00" : end}`
    );

    const latestStartTime = new Date(endDate);
    latestStartTime.setHours(latestStartTime.getHours() - durationHours);
    if (startDate > latestStartTime) return;

    let current = new Date(startDate);
    while (current <= latestStartTime) {
      const blockStart = `${current.getHours().toString().padStart(2, "0")}:00`;
      const endBlockDate = new Date(current);
      endBlockDate.setHours(endBlockDate.getHours() + durationHours);
      const endHour = endBlockDate.getHours();
      const blockEnd =
        endHour === 0 || endHour === 24
          ? "24:00"
          : `${endHour.toString().padStart(2, "0")}:00`;

      if (isBookingRangeAvailable(dateString, blockStart, durationHours, bookingStudioData)) {
        blocks.push({ start: blockStart, end: blockEnd });
      }

      current.setHours(current.getHours() + 1);
    }
  });

  const seen = new Set();
  const uniqueBlocks = [];
  blocks.forEach((b) => {
    const key = `${b.start}-${b.end}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueBlocks.push(b);
    }
  });

  uniqueBlocks.sort((a, b) => (a.start > b.start ? 1 : -1));
  return uniqueBlocks;
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "";
  const d = parseLocalDateString(dateString);
  if (!d) return "";
  return format(d, "EEEE, dd.MM.yyyy", { locale: de });
};

export const TimePickerPacket = ({ bookingStudioData, nextSlide }) => {
  const { bookingData, updateBookingData } = useBooking();

  const selectedDate = bookingData?.date || "";
  const durationHours = getBookingDurationHours(bookingStudioData, bookingData);
  const [selectedKey, setSelectedKey] = useState(null);

  // ---- Info texts for weekend surcharge & extra fees (e.g. cleaning) ----
  // Mirrors calculateStudioPrice.js: uses Europe/Berlin timezone for day resolution,
  // picks the correct Saturday vs Sunday config, and formats by surchargeType.
  const weekendSurchargeLabel = useMemo(() => {
    if (!selectedDate || !bookingStudioData?.weekendSurcharge) return '';

    const d = parseLocalDateString(selectedDate);
    if (!d || Number.isNaN(d.getTime())) return '';

    // Resolve weekday in Europe/Berlin — same as the backend Lambda and calculateStudioPrice.js
    const localDayStr = d.toLocaleString('en-US', { weekday: 'short', timeZone: 'Europe/Berlin' });
    const DAY_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const day = DAY_MAP[localDayStr] ?? d.getDay();
    const ws = bookingStudioData.weekendSurcharge;

    let value = 0;
    let type = '';
    if (day === 6 && ws.saturdayEnabled) {
      value = Number(ws.saturdaySurchargeValue) || 0;
      type = ws.saturdaySurchargeType || '';
    } else if (day === 0 && ws.sundayEnabled) {
      value = Number(ws.sundaySurchargeValue) || 0;
      type = ws.sundaySurchargeType || '';
    }

    if (!value) return '';

    const isPercentage = type === 'PERCENTAGE' || type === 'percentage';
    const formatted = isPercentage ? `${value}%` : `${value} €`;
    return `Zzgl. ${formatted} Wochenendaufschlag`;
  }, [selectedDate, bookingStudioData?.weekendSurcharge]);

  const extraFeesLabel = useMemo(() => {
    const fees = bookingStudioData?.extraFees;
    if (!Array.isArray(fees) || fees.length === 0) return "";

    // Try to surface the fee names (e.g. "Reinigungsgebühr"), but keep it short
    const names = Array.from(
      new Set(
        fees
          .map((f) => f.name || f.description)
          .filter(Boolean)
      )
    );

    if (names.length === 0) return "Zzgl. fester Gebühren.";
    if (names.length === 1) return `Zzgl. fester Gebühr (${names[0]}).`;
    return `Zzgl. fester Gebühren (${names.join(", ")}).`;
  }, [bookingStudioData?.extraFees]);

  const timeBlocks = useMemo(
    () => generateBlocksForDate(selectedDate, durationHours, bookingStudioData),
    [selectedDate, durationHours, bookingStudioData]
  );

  // This step precedes Creative and Equipment selection. Keep its total aligned
  // with the studio, surcharge, discount, and fixed-fee rows available here.
  const totalPrice = selectedKey
    ? calculatePreExtrasTotal(bookingData)
    : 0;

  const handleSelectBlock = (block) => {
    const key = `${block.start}-${block.end}`;
    setSelectedKey(key);
    updateBookingData("startTime", block.start);
    updateBookingData("endTime", block.end);
  
  };

  const findDateWithBlocks = (direction) => {
    if (!selectedDate || !durationHours) return;

    const start = parseLocalDateString(selectedDate);
    if (!start) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 1; i <= 60; i++) {
      const candidate = new Date(start);
      candidate.setDate(start.getDate() + direction * i);

      // Never go before today – mirrors the DateChooser behaviour of not allowing past dates
      const candidateMidnight = new Date(candidate);
      candidateMidnight.setHours(0, 0, 0, 0);
      if (direction < 0 && candidateMidnight < today) {
        break;
      }

      const candidateStr = formatLocalDate(candidate);
      const blocks = generateBlocksForDate(
        candidateStr,
        durationHours,
        bookingStudioData
      );
      // Only select dates that actually have valid blocks – all others are skipped
      if (blocks.length > 0) {
        updateBookingData("date", candidateStr);
        setSelectedKey(null);
        return;
      }
    }
  };

  const handlePrevDate = () => findDateWithBlocks(-1);
  const handleNextDate = () => findDateWithBlocks(1);

  const handleContinue = () => {
    if (!selectedKey) return;
    nextSlide();
  };

  const isDisabled = !selectedKey || timeBlocks.length === 0;

  return (
    <div className={classes.container}>
      <header className={classes.header}>
          <button
            type="button"
            className={classes.arrowButton}
            onClick={handlePrevDate}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8"
              height="14"
              viewBox="0 0 8 14"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0 6.51266L6.4108 0L7.5 1.14765L2.1784 6.99949L7.5383 12.7698L6.41006 14L0.0118651 6.5246L0 6.51266Z"
                fill="#1F1F1F"
              />
            </svg>
          </button>
          <div className={classes.dateInfoContainer}>
            <span className={`${classes.dateLabel} regularSmall`}>
              {formatDisplayDate(selectedDate)}
            </span>
            {(weekendSurchargeLabel || extraFeesLabel) && (
              <span className={`${classes.dateInfoText} slimRegularSmallText`}>
                {weekendSurchargeLabel}
                {weekendSurchargeLabel && extraFeesLabel && " · "}
                {extraFeesLabel}
              </span>
            )}
          </div>
          <button
            type="button"
            className={classes.arrowButton}
            onClick={handleNextDate}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8"
              height="14"
              viewBox="0 0 8 14"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.5 6.51266L1.0892 0L0 1.14765L5.3216 6.99949L-0.0383005 12.7698L1.08994 14L7.48813 6.5246L7.5 6.51266Z"
                fill="#1F1F1F"
              />
            </svg>
          </button>
      </header>

      <div className={classes.listContainer}>
        {timeBlocks.length === 0 ? (
          <p className="regularSmall" style={{ color: "var(--dark-grey-3)" }}>
            Für dieses Datum sind keine Zeitfenster verfügbar.
          </p>
        ) : (
          timeBlocks.map((block) => {
            const key = `${block.start}-${block.end}`;
            const isSelected = key === selectedKey;
            return (
              <button
                key={key}
                type="button"
                className={`${classes.timeOption} ${
                  isSelected ? classes.selected : ""
                }`}
                onClick={() => handleSelectBlock(block)}
              >
                <span className="regularLarge">
                  {block.start} - {block.end} Uhr
                </span>
              </button>
            );
          })
        )}
      </div>

      <div className={classes.footer}>
        <div className={classes.totalPrice}>
          <span className="semiboldSmall">Gesamtbetrag</span>
          <span
            style={{ color: "var(--dark-grey-3)" }}
            className="regularSmall"
          >
            {totalPrice}€
          </span>
        </div>
        <button
          className={`${classes.continueButton} regularLarge ${
            isDisabled ? classes.disabled : ""
          }`}
          onClick={handleContinue}
          disabled={isDisabled}
        >
          Termin finden
        </button>
      </div>
    </div>
  );
};

export default TimePickerPacket;
